import type { FC } from 'react';
import React, { useEffect, useContext, useState } from 'react';
import { Box } from '@mui/system';

import { AppContext } from '../../../contextStore/AppContext';
import noop from '../../../../utils/function/noop';
import type { PaymentMethodError } from '../../../../services/stripe/types';
import {
  getCustomerId,
  getCardCategories,
} from '../../../../utils/session/selectors';
import PayAndStoreStripeForm from '../PayAndStoreStripeForm';
import type {
  PayAndStoreCardContainerProps,
  PayAndStoreStripeFormSubmitData,
} from '../../types';
import { NotificationContext } from '../../../contextStore/NotificationContext';
import useSessionExpirationReminder from '../../../../hooks/useSessionExpirationReminder';
import StripePaymentElementWrapper from '../../../../components/stripe/stripe-elements-wrapper/StripePaymentElementWrapper';
import processCardPaymentStripe from '../../../../utils/capabilities/processCardPaymentStripe';
import { shouldShowSaveOptions } from '../../utils/showPaymentMethodSaveSectionUtil';
import { getMedicationOptionsFromManufacturerCardTypeConfig } from '../../../../utils/session/getMedicationOptionsFromManufacturerCardTypeConfig';
import { isManufacturerCardFeatureEnabledForMerchant } from '../../../checkout/components/Wallet/utils/isManufacturerCardEnabledForMerchant';
import { isManufacturerCardTypeEnabled } from '../../../add-payment-method/utils/isManufacturerCardTypeEnabled';
import type { CardCategoriesConfig } from '../../../../shared/types/CardCategories/CardCategoriesConfig';

const PayAndStoreCardContainer: FC<
  PayAndStoreCardContainerProps
> = ({
  vendorPlatformKey,
  merchantTransactionId,
  amount,
  paymentDescription,
  authorizeCard,
  formTitle,
  statementDescriptorSuffix,
  showSaveFeatureBlock,
  paymentMethodSaveMode,
  isInFocus,
  stripeApi,
  paymentApi,
  onSuccess = noop,
  onCancel = noop,
  onError = noop,
  onBeforeSubmit = noop,
  onLoad = noop,
  onLoadComplete = noop,
  onBackClick,
}) => {
  const {
    initialSessionData,
    setData,
    originalCheckoutSessionResponse,
  } = useContext(AppContext);
  const [isMakingPayment, setIsMakingPayment] = useState(false);

  const { notify } = useContext(NotificationContext);

  const medicationOptions =
    getMedicationOptionsFromManufacturerCardTypeConfig(
      initialSessionData,
    );

  const cardCategoriesConfig: CardCategoriesConfig =
    getCardCategories(initialSessionData);

  const isManufacturerCardEnabledForMerchant =
    isManufacturerCardFeatureEnabledForMerchant(
      initialSessionData,
    ) && isManufacturerCardTypeEnabled(cardCategoriesConfig);

  useSessionExpirationReminder({
    notify,
    originalCheckoutSessionResponse,
  });

  const customerId = getCustomerId(
    originalCheckoutSessionResponse,
  );
  const isGuestUser = !customerId;

  const shouldShowSaveOptionsSection = shouldShowSaveOptions({
    isGuestUser,
    paymentMethodSaveMode,
    showSaveFeatureBlock,
  });

  const onProcessingComplete = () => {
    setIsMakingPayment(false);
    setData({ overlayLoaderConfig: { show: false } });
  };

  const handlePaymentMethodError = ({
    paymentMethodError,
  }: {
    paymentMethodError: PaymentMethodError;
  }) => {
    const { errorType, message } = paymentMethodError;

    if (errorType !== 'validation_error') {
      console.error(message);
      onError(message);
    }
    onProcessingComplete();
  };

  const onSubmit = ({
    elements,
    formData,
    stripe,
  }: PayAndStoreStripeFormSubmitData) => {
    onBeforeSubmit();
    setIsMakingPayment(true);
    setData({ overlayLoaderConfig: { show: true } });

    processCardPaymentStripe({
      elements,
      formData,
      stripe,
      originalCheckoutSessionResponse,
      amount,
      merchantTransactionId,
      authorizeCard,
      statementDescriptorSuffix,
      paymentDescription,
      vendorPlatformKey,
      stripeApi,
      paymentApi,
      medicationOptions,
      onSuccess,
      onProcessingComplete,
      handlePaymentMethodError,
    }).catch(() => {
      // ignore
    });
  };

  useEffect(() => {
    onLoad();
  }, []);

  return vendorPlatformKey ? (
    <Box>
      <StripePaymentElementWrapper
        vendorPlatformKey={vendorPlatformKey}
      >
        <PayAndStoreStripeForm
          amount={amount}
          isMakingPayment={isMakingPayment}
          paymentMethodSaveMode={paymentMethodSaveMode}
          shouldShowSaveOptionsSection={
            shouldShowSaveOptionsSection
          }
          formTitle={formTitle}
          isInFocus={isInFocus}
          stripeApi={stripeApi}
          onBackClick={onBackClick}
          setIsMakingPayment={setIsMakingPayment}
          onLoadComplete={onLoadComplete}
          onSubmit={onSubmit}
          onCancel={onCancel}
          medicationOptions={medicationOptions}
          cardCategoriesConfigObject={cardCategoriesConfig}
          isManufacturerCardEnabledForMerchant={
            isManufacturerCardEnabledForMerchant
          }
        />
      </StripePaymentElementWrapper>
    </Box>
  ) : null;
};

export default PayAndStoreCardContainer;
